import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import AssignmentReturnRoundedIcon from "@mui/icons-material/AssignmentReturnRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import { useTranslation } from "react-i18next";
import returnRequestService from "../../../services/ReturnRequestService";
import saleOrderServices, { parseCatalogProducts } from "../../../services/SalesOrderService";
import { formatCodAmount, formatCodDate } from "../../../utils/codUtils";
import { ReportDateRange } from "../../../utils/codReports";
import { exportExcelWorkbook, reportFileStamp } from "../../../utils/codReportExcel";
import {
  approvalRate,
  buildReturnCustomerRows,
  buildReturnProductRows,
  buildReturnReasonRows,
  lineValue,
  normalizeReturn,
  ReturnBucket,
  ReturnRecord,
  scopeReturns,
  sumReturnTotals,
} from "../../../utils/opsReports";
import { localizeReturnReason } from "../../../helper/localizeReturnReason";
import { showToast } from "../../component/common/toastNotification";
import PageHeader from "../../../components/ui/PageHeader";
import CodStatCard from "../../../components/ui/CodStatCard";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import StatusChip from "../../../components/ui/StatusChip";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import CodTabBar from "../codSalesRep/CodTabBar";
import { SupervisorChartsSection } from "./OpsReportCharts";
import {
  MoneyStat,
  onActivate,
  reportRowHeaderSx,
  reportRowSx,
  ReportToolbar,
} from "../codSalesRep/CodReportParts";
import { NJIM, pageShellSx } from "../codSalesRep/codStyles";

type View = "customer" | "product" | "reason" | "charts";

const panelSx = {
  borderRadius: "16px",
  border: `1px solid ${NJIM.border}`,
  background: NJIM.white,
  boxShadow: NJIM.elevation,
  p: { xs: 2, md: 3 },
} as const;

const miniRowSx = {
  display: "grid",
  alignItems: "center",
  gap: { xs: 1, sm: 2 },
  px: 1.5,
  py: 1.1,
  borderRadius: "10px",
  bgcolor: NJIM.muted,
  border: `1px solid ${NJIM.border}`,
} as const;

const SupervisorReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<ReturnRecord[]>([]);
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [range, setRange] = useState<ReportDateRange>({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [view, setView] = useState<View>("customer");
  const [expandedKey, setExpandedKey] = useState("");
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [returnsRes, catalogRes] = await Promise.allSettled([
        returnRequestService.getReturnList({
          keyword: "",
          pageNumber: 1,
          pageSize: 5000,
          sortBy: "RequestDate",
          sortDescending: true,
        }),
        saleOrderServices.getProductCatalog(),
      ]);
      if (returnsRes.status === "fulfilled") {
        const items = (returnsRes.value?.data?.items ?? []) as Record<string, unknown>[];
        setRecords(items.map(normalizeReturn));
      } else {
        showToast(t("opsReports.loadFailed"), "error");
      }
      if (catalogRes.status === "fulfilled") {
        const map: Record<string, string> = {};
        parseCatalogProducts(catalogRes.value?.data).forEach((p) => {
          const name = String(p.name ?? p.Name ?? p.title ?? p.Title ?? "").trim();
          const id = String(p.id ?? p.Id ?? "").trim();
          const code = String(p.code ?? p.Code ?? "").trim();
          if (name && id) map[id] = name;
          if (name && code) map[code] = name;
        });
        setProductNames(map);
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const productName = useCallback(
    (itemId: string) => productNames[itemId] || itemId || "—",
    [productNames]
  );
  const reasonLabel = useCallback((reason: string) => localizeReturnReason(reason, t), [t]);
  const statusLabel = useCallback((status: ReturnBucket) => t(`opsReports.${status}`), [t]);

  /** Date range + pending toggle + search (customer name/code or return no). */
  const scoped = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return scopeReturns(records, range).filter((record) => {
      if (onlyPending && record.status !== "pending") return false;
      if (!keyword) return true;
      return (
        record.customerName.toLowerCase().includes(keyword) ||
        record.customerCode.toLowerCase().includes(keyword) ||
        record.returnNumber.toLowerCase().includes(keyword) ||
        record.items.some((line) => productName(line.itemId).toLowerCase().includes(keyword))
      );
    });
  }, [records, range, onlyPending, search, productName]);

  const customerRows = useMemo(() => buildReturnCustomerRows(scoped), [scoped]);
  const productRows = useMemo(() => buildReturnProductRows(scoped, productName), [scoped, productName]);
  const reasonRows = useMemo(() => buildReturnReasonRows(scoped), [scoped]);
  const totals = useMemo(() => sumReturnTotals(customerRows), [customerRows]);
  const maxReasonQty = reasonRows[0]?.quantity ?? 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      const lines = scoped.flatMap((record) =>
        record.items.map((line) => ({
          returnNo: record.returnNumber,
          date: record.requestDate,
          customerCode: record.customerCode,
          customerName: record.customerName,
          status: statusLabel(record.status),
          productCode: line.itemId,
          product: productName(line.itemId),
          batch: line.batchNumber,
          reason: reasonLabel(line.reason),
          qty: line.quantity,
          price: line.price,
          value: lineValue(line),
        }))
      );
      await exportExcelWorkbook(
        `Returns-Report_${reportFileStamp()}`,
        [
          {
            name: t("opsReports.sheetReturnsByCustomer"),
            columns: [
              { key: "customerCode", header: t("opsReports.customerCode"), width: 18 },
              { key: "customerName", header: t("opsReports.customerName"), width: 36 },
              { key: "requests", header: t("opsReports.requests"), type: "int" },
              { key: "pending", header: t("opsReports.pending"), type: "int" },
              { key: "approved", header: t("opsReports.approved"), type: "int" },
              { key: "rejected", header: t("opsReports.rejected"), type: "int" },
              { key: "cancelled", header: t("opsReports.cancelled"), type: "int" },
              { key: "quantity", header: t("opsReports.quantity"), type: "int" },
              { key: "requestedValue", header: t("opsReports.requestedValue"), type: "money" },
              { key: "approvedValue", header: t("opsReports.approvedValue"), type: "money" },
              { key: "rejectedValue", header: t("opsReports.rejectedValue"), type: "money" },
              { key: "pendingValue", header: t("opsReports.pendingValue"), type: "money" },
              { key: "rate", header: t("opsReports.approvalRate"), type: "percent" },
              { key: "lastRequest", header: t("opsReports.lastRequest"), type: "date" },
            ],
            rows: customerRows.map((row) => ({ ...row, rate: approvalRate(row) })),
            totals: {
              customerCode: t("codReports.totalsRow"),
              requests: totals.requests,
              pending: totals.pending,
              approved: totals.approved,
              rejected: totals.rejected,
              cancelled: totals.cancelled,
              quantity: totals.quantity,
              requestedValue: totals.requestedValue,
              approvedValue: totals.approvedValue,
              rejectedValue: totals.rejectedValue,
              pendingValue: totals.pendingValue,
              rate: approvalRate(totals),
            },
          },
          {
            name: t("opsReports.sheetReturns"),
            columns: [
              { key: "returnNo", header: t("opsReports.returnNo"), width: 22 },
              { key: "date", header: t("opsReports.requestDate"), type: "datetime" },
              { key: "customerCode", header: t("opsReports.customerCode"), width: 18 },
              { key: "customerName", header: t("opsReports.customerName"), width: 36 },
              { key: "status", header: t("codReports.status"), width: 14 },
              { key: "items", header: t("opsReports.lines"), type: "int" },
              { key: "quantity", header: t("opsReports.quantity"), type: "int" },
              { key: "value", header: t("opsReports.value"), type: "money" },
              { key: "rejectionReason", header: t("opsReports.rejectionReason"), width: 28 },
              { key: "rejectionNote", header: t("opsReports.rejectionNote"), width: 28 },
              { key: "notes", header: t("opsReports.notes"), width: 30 },
            ],
            rows: scoped.map((record) => ({
              returnNo: record.returnNumber,
              date: record.requestDate,
              customerCode: record.customerCode,
              customerName: record.customerName,
              status: statusLabel(record.status),
              items: record.items.length,
              quantity: record.items.reduce((sum, line) => sum + line.quantity, 0),
              value: record.amount,
              rejectionReason: record.rejectionReason,
              rejectionNote: record.rejectionNote,
              notes: record.additionalNotes,
            })),
            totals: { returnNo: t("codReports.totalsRow"), quantity: totals.quantity, value: totals.requestedValue },
          },
          {
            name: t("opsReports.sheetReturnLines"),
            columns: [
              { key: "returnNo", header: t("opsReports.returnNo"), width: 22 },
              { key: "date", header: t("opsReports.requestDate"), type: "datetime" },
              { key: "customerCode", header: t("opsReports.customerCode"), width: 18 },
              { key: "customerName", header: t("opsReports.customerName"), width: 34 },
              { key: "status", header: t("codReports.status"), width: 14 },
              { key: "productCode", header: t("opsReports.productCode"), width: 26 },
              { key: "product", header: t("opsReports.product"), width: 40 },
              { key: "batch", header: t("opsReports.batch"), width: 16 },
              { key: "reason", header: t("opsReports.reason"), width: 24 },
              { key: "qty", header: t("opsReports.quantity"), type: "int" },
              { key: "price", header: t("codReports.unitPrice"), type: "money" },
              { key: "value", header: t("opsReports.value"), type: "money" },
            ],
            rows: lines,
            totals: {
              returnNo: t("codReports.totalsRow"),
              qty: lines.reduce((sum, line) => sum + line.qty, 0),
              value: lines.reduce((sum, line) => sum + line.value, 0),
            },
          },
          {
            name: t("opsReports.sheetByProduct"),
            columns: [
              { key: "itemId", header: t("opsReports.productCode"), width: 26 },
              { key: "name", header: t("opsReports.product"), width: 40 },
              { key: "requests", header: t("opsReports.requests"), type: "int" },
              { key: "quantity", header: t("opsReports.quantity"), type: "int" },
              { key: "approvedQuantity", header: t("opsReports.approvedQty"), type: "int" },
              { key: "rejectedQuantity", header: t("opsReports.rejectedQty"), type: "int" },
              { key: "value", header: t("opsReports.value"), type: "money" },
              { key: "batches", header: t("opsReports.batches"), width: 30 },
              { key: "reasons", header: t("opsReports.reasons"), width: 40 },
            ],
            rows: productRows.map((row) => ({
              ...row,
              batches: row.batches.join(", "),
              reasons: row.reasons.map(reasonLabel).join(", "),
            })),
          },
          {
            name: t("opsReports.sheetByReason"),
            columns: [
              { key: "reason", header: t("opsReports.reason"), width: 32 },
              { key: "lines", header: t("opsReports.lines"), type: "int" },
              { key: "quantity", header: t("opsReports.quantity"), type: "int" },
              { key: "value", header: t("opsReports.value"), type: "money" },
            ],
            rows: reasonRows.map((row) => ({ ...row, reason: reasonLabel(row.reason) })),
          },
        ],
        { rtl: i18n.language?.startsWith("ar") }
      );
      showToast(t("codReports.exportSuccess"), "success");
    } catch {
      showToast(t("codReports.exportFailed"), "error");
    } finally {
      setExporting(false);
    }
  };

  const tabs = [
    { value: "customer" as const, label: t("opsReports.byCustomer"), icon: <GroupsRoundedIcon sx={{ fontSize: 18 }} />, count: customerRows.length },
    { value: "product" as const, label: t("opsReports.byProduct"), icon: <Inventory2RoundedIcon sx={{ fontSize: 18 }} />, count: productRows.length },
    { value: "reason" as const, label: t("opsReports.byReason"), icon: <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />, count: reasonRows.length },
    { value: "charts" as const, label: t("codReports.chartsTab"), icon: <InsertChartRoundedIcon sx={{ fontSize: 18 }} /> },
  ];

  const empty = !loading && scoped.length === 0;

  return (
    <Box sx={{ ...pageShellSx, maxWidth: 1440, mx: "auto" }}>
      <PageHeader title={t("opsReports.supervisorTitle")} subtitle={t("opsReports.supervisorSubtitle")} />

      <Box sx={panelSx}>
        <ReportToolbar
          range={range}
          onRangeChange={setRange}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("opsReports.searchReturns")}
          onlyBalance={onlyPending}
          onOnlyBalanceChange={setOnlyPending}
          toggleLabel={t("opsReports.onlyPending")}
          onExport={handleExport}
          exporting={exporting}
          exportDisabled={loading || scoped.length === 0}
        />

        <Box sx={{ containerType: "inline-size" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
              mb: 3,
              "@container (min-width: 620px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
              "@container (min-width: 980px)": { gridTemplateColumns: "repeat(5, minmax(0, 1fr))" },
            }}
          >
            <CodStatCard label={t("opsReports.requests")} value={totals.requests} tone="ink" icon={<AssignmentReturnRoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.pending")} value={totals.pending} tone="warning" icon={<HourglassTopRoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.approvedValue")} value={formatCodAmount(totals.approvedValue)} tone="success" icon={<TaskAltRoundedIcon />} loading={loading} compactValue />
            <CodStatCard label={t("opsReports.rejected")} value={totals.rejected} tone="danger" icon={<CancelRoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.approvalRate")} value={`${(approvalRate(totals) * 100).toFixed(0)}%`} tone="info" icon={<PercentRoundedIcon />} loading={loading} />
          </Box>
        </Box>

        <CodTabBar value={view} tabs={tabs} onChange={setView} />

        {loading ? (
          <TableSkeleton rows={6} columns={5} />
        ) : empty ? (
          <EmptyState title={t("opsReports.noData")} description={t("opsReports.noDataHint")} />
        ) : view === "charts" ? (
          <SupervisorChartsSection records={scoped} productName={productName} reasonLabel={reasonLabel} />
        ) : view === "customer" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {customerRows.map((row) => {
              const isOpen = expandedKey === row.key;
              const toggle = () => setExpandedKey(isOpen ? "" : row.key);
              return (
                <Box key={row.key} sx={reportRowSx(isOpen)}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onClick={toggle}
                    onKeyDown={onActivate(toggle)}
                    sx={{
                      ...reportRowHeaderSx,
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr) auto",
                        md: "minmax(0, 1.6fr) repeat(3, minmax(0, 1fr)) auto",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                      <CodInitialsAvatar name={row.customerName || row.customerCode} size={40} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.customerName}>
                          {row.customerName || row.customerCode || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {row.customerCode} · {t("opsReports.requests")}: {row.requests}
                          {row.pending > 0 ? ` · ${t("opsReports.pending")}: ${row.pending}` : ""}
                          {row.lastRequest ? ` · ${t("opsReports.lastRequest")} ${formatCodDate(row.lastRequest)}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                      <MoneyStat label={t("opsReports.requestedValue")} value={row.requestedValue} />
                    </Box>
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                      <MoneyStat label={t("opsReports.approvedValue")} value={row.approvedValue} color={NJIM.success} />
                    </Box>
                    <MoneyStat
                      label={t("opsReports.pendingValue")}
                      value={row.pendingValue}
                      color={row.pendingValue > 0 ? NJIM.warning : NJIM.textMuted}
                    />
                    <KeyboardArrowDownRoundedIcon
                      sx={{
                        display: { xs: "none", md: "block" },
                        color: NJIM.textSecondary,
                        transform: isOpen ? "rotate(180deg)" : "none",
                        transition: `transform 180ms ${NJIM.easeOut}`,
                      }}
                    />
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ p: { xs: 1.5, md: 2 }, borderTop: `1px solid ${NJIM.border}`, display: "flex", flexDirection: "column", gap: 1 }}>
                      {row.records.map((record) => (
                        <Box key={record.id} sx={{ ...miniRowSx, gridTemplateColumns: "1fr", gap: 0.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary }}>
                                {record.returnNumber || "—"}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                                {formatCodDate(record.requestDate)} · {record.items.length} {t("opsReports.lines").toLowerCase()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <StatusChip status={record.status} label={statusLabel(record.status)} />
                              <Typography sx={{ fontSize: 14, fontWeight: 800, color: NJIM.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                {formatCodAmount(record.amount)}
                              </Typography>
                            </Box>
                          </Box>
                          {record.items.map((line, index) => (
                            <Box key={`${record.id}-${index}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 12, color: NJIM.textSecondary, flexWrap: "wrap" }}>
                              <span>
                                <strong style={{ color: NJIM.textPrimary }}>{productName(line.itemId)}</strong>
                                {line.batchNumber ? ` · ${t("opsReports.batch")} ${line.batchNumber}` : ""}
                                {` · ${reasonLabel(line.reason)}`}
                              </span>
                              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                                × {line.quantity} · {formatCodAmount(lineValue(line))}
                              </span>
                            </Box>
                          ))}
                          {record.status === "rejected" && (record.rejectionReason || record.rejectionNote) && (
                            <Typography sx={{ fontSize: 12, color: NJIM.danger }}>
                              {t("opsReports.rejectionReason")}: {record.rejectionReason}
                              {record.rejectionNote ? ` — ${record.rejectionNote}` : ""}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        ) : view === "product" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {productRows.map((row) => (
              <Box
                key={row.key}
                sx={{
                  ...miniRowSx,
                  bgcolor: NJIM.white,
                  gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "minmax(0, 2fr) repeat(4, minmax(0, 0.8fr))" },
                  py: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.name}>
                    {row.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {t("opsReports.requests")}: {row.requests}
                    {row.reasons.length ? ` · ${row.reasons.map(reasonLabel).join(", ")}` : ""}
                  </Typography>
                </Box>
                <Stat label={t("opsReports.quantity")} value={row.quantity} />
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Stat label={t("opsReports.approvedQty")} value={row.approvedQuantity} color={NJIM.success} />
                </Box>
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Stat label={t("opsReports.rejectedQty")} value={row.rejectedQuantity} color={NJIM.danger} />
                </Box>
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <MoneyStat label={t("opsReports.value")} value={row.value} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {reasonRows.map((row) => (
              <Box key={row.key} sx={{ ...miniRowSx, bgcolor: NJIM.white, gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "minmax(0, 1.2fr) minmax(0, 2fr) repeat(2, minmax(0, 0.7fr))" }, py: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary }}>{reasonLabel(row.reason)}</Typography>
                <Box sx={{ display: { xs: "none", md: "block" }, height: 8, borderRadius: 999, bgcolor: NJIM.muted, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${maxReasonQty ? Math.max(4, (row.quantity / maxReasonQty) * 100) : 0}%`, bgcolor: NJIM.mint, borderRadius: 999 }} />
                </Box>
                <Stat label={t("opsReports.quantity")} value={row.quantity} />
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <MoneyStat label={t("opsReports.value")} value={row.value} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Stat: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color = NJIM.textPrimary }) => (
  <Box sx={{ textAlign: "end", minWidth: 0 }}>
    <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>
      {value.toLocaleString("en-US")}
    </Typography>
  </Box>
);

export default SupervisorReportsPage;
